'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Group, MultiSelect, Select, Stack, TextInput } from '@mantine/core';
import { analytics } from '@modules/analytics';
import { GoogleTagManagerUtils } from '@modules/google-tag-manager/google-tag-manager-utils';
import { createChildLogger } from '@modules/logger';
import { AnimatePresence, motion } from 'motion/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  CHILI_PIPER_DOMAIN,
  CHILI_PIPER_ROUTER,
  CHILI_PIPER_TRIGGER,
  buildChiliPiperLead,
  getChiliPiperErrorMessage,
  hideChiliPiperFrameUntilRouted,
  showChiliPiperFrame,
} from '../_lib/chili-piper';
import {
  CompanySizeOptions,
  type ContactSalesData,
  DbcAmountOptions,
  EventAttendanceOptions,
  HearAboutUsLabels,
  HearAboutUsOptions,
  ProductInterestOptions,
  type Region,
  RegionOptions,
  contactSalesSchema,
} from '../_lib/validators';
import { upsertHubspotEmail } from '../actions';
import { introBody, titleHeading } from '../styles.css';

const log = createChildLogger({ component: 'ContactSalesForm' });
const THANK_YOU_BOOKED = '/onboarding/contact-sales/thank-you?demo-booked=true';
// concierge.js doesn't auto-close the modal after booking, so we drive the close
// ourselves via closePopup(). Hold the confirmation screen ~5s so the user sees it.
const CHILI_PIPER_CLOSE_DELAY_MS = 5000;

// Match the Select theme's dropdown size so MultiSelect options look identical to Select options.
const multiSelectComboboxProps = { size: 'md' } as const;

export const ContactSalesForm: React.FC<{
  region?: Region;
}> = ({ region }) => {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [serverError, setServerError] = useState<string | null>(null);
  const [chiliPiperLoading, setChiliPiperLoading] = useState(false);

  const {
    handleSubmit,
    setError,
    setFocus,
    register,
    setValue,
    control,
    formState: { errors },
    watch,
    reset,
  } = useForm<ContactSalesData>({
    resolver: zodResolver(contactSalesSchema),
    mode: 'onSubmit',
    defaultValues: {
      step: 1,
      email: '',
    },
  });

  const step = watch('step');

  useEffect(() => {
    GoogleTagManagerUtils.loadGtm();
  }, []);

  // Prefetch the thank-you page so the client-side transition from the modal is instant.
  useEffect(() => {
    router.prefetch(THANK_YOU_BOOKED);
  }, [router]);

  useEffect(() => {
    if (step === 2) {
      setFocus('firstName');
      if (region) {
        setValue('region', region);
      }
    }
  }, [step, setFocus, setValue, region]);

  const productInterest = (step === 2 ? watch('productInterest') : undefined) ?? [];
  const showEventAttendance = productInterest.includes('Event Lead Capture');
  const showDbcAmount = productInterest.includes('Digital Business Cards');

  const onSubmit = handleSubmit((formData) => {
    setServerError(null);

    if (formData.step === 1) {
      const email = formData.email;
      startTransition(async () => {
        const result = await upsertHubspotEmail(email);
        if (!result.success) {
          setError('root.serverError', { message: result.error });
          return;
        }

        GoogleTagManagerUtils.setUserData({ email });
        GoogleTagManagerUtils.sendFormSubmitEvent();
        analytics.identify({ traits: { email } });
        analytics.track({ name: 'demo_onboarding_email_provided' });

        reset({
          step: 2,
          email,
          firstName: '',
          lastName: '',
          jobTitle: '',
          companyName: '',
          productInterest: [],
          hearAboutUs: [],
        });
      });
      return;
    }

    if (typeof window === 'undefined' || !window.ChiliPiper) {
      log.error('Chili Piper unavailable');
      setServerError('Booking is unavailable right now. Please refresh and try again.');
      return;
    }

    setChiliPiperLoading(true);
    hideChiliPiperFrameUntilRouted();

    // Open the booking modal first so the user sees it immediately.
    window.ChiliPiper.submit(CHILI_PIPER_DOMAIN, CHILI_PIPER_ROUTER, {
      trigger: CHILI_PIPER_TRIGGER,
      lead: buildChiliPiperLead(formData),
      onRouted: () => {
        showChiliPiperFrame();
        setChiliPiperLoading(false);
      },
      // Navigate in the background (prefetched, instant) so the thank-you page is
      // already rendered underneath by the time we dismiss the modal. After the delay,
      // close the modal ourselves — concierge.js has no built-in auto-close.
      onSuccess: () => {
        showChiliPiperFrame();
        router.push(THANK_YOU_BOOKED);
        setTimeout(() => {
          window.ChiliPiper?.closePopup();
        }, CHILI_PIPER_CLOSE_DELAY_MS);
      },
      onError: (error) => {
        const errorMessage = getChiliPiperErrorMessage(error);
        log.error('Chili Piper routing failed', { error, error_message: errorMessage });
        showChiliPiperFrame();
        setChiliPiperLoading(false);
        setServerError(errorMessage);
      },
      onClose: () => {
        showChiliPiperFrame();
        setChiliPiperLoading(false);
      },
    });

    analytics.track({
      name: 'demo_onboarding_details_provided',
      context: { google_ads_conversion_id: 7087104892 },
    });
  });

  const submitLabel = step === 1 ? 'Continue' : 'Request demo';
  const submitLoading = step === 1 ? pending : chiliPiperLoading;

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack gap="xl">
        <Stack gap="md">
          <h1 className={titleHeading}>Let's meet</h1>
          <p className={introBody}>Fill in the form and pick a time that works for you.</p>
        </Stack>
        <Stack gap="md">
          <input type="hidden" {...register('step')} />
          <TextInput
            label="Company Email"
            type="email"
            placeholder="Enter your company email"
            data-testid="email-input"
            disabled={step === 2}
            error={getEmailError(errors)}
            {...register('email')}
          />

          <AnimatePresence>
            {step === 2 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.3, ease: 'easeInOut' },
                  opacity: { duration: 0.5, ease: 'easeInOut' },
                }}
              >
                <Stack gap="md">
                  <Group grow gap="md">
                    <TextInput
                      label="First Name"
                      placeholder="Enter your first name"
                      error={getStep2Error(errors, 'firstName')}
                      {...register('firstName')}
                    />
                    <TextInput
                      label="Last Name"
                      placeholder="Enter your last name"
                      error={getStep2Error(errors, 'lastName')}
                      {...register('lastName')}
                    />
                  </Group>
                  <TextInput
                    label="Job Title"
                    placeholder="Enter your job title"
                    error={getStep2Error(errors, 'jobTitle')}
                    {...register('jobTitle')}
                  />
                  <TextInput
                    label="Company Name"
                    placeholder="Enter your company name"
                    error={getStep2Error(errors, 'companyName')}
                    {...register('companyName')}
                  />
                  <Controller
                    name="companySize"
                    control={control}
                    render={({ field }) => (
                      <Select
                        label="Company Size (Employees)"
                        placeholder="Select company size"
                        data={CompanySizeOptions.map((size) => ({ value: size, label: size }))}
                        error={getStep2Error(errors, 'companySize')}
                        value={field.value ?? null}
                        onChange={(value) => field.onChange(value ?? undefined)}
                      />
                    )}
                  />
                  <Controller
                    name="region"
                    control={control}
                    render={({ field }) => (
                      <Select
                        label="What region are you located in?"
                        placeholder="Select your region"
                        data={RegionOptions.map((value) => ({ value, label: value }))}
                        error={getStep2Error(errors, 'region')}
                        value={field.value ?? null}
                        onChange={(value) => field.onChange(value ?? undefined)}
                      />
                    )}
                  />
                  <Controller
                    name="productInterest"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        label="What product are you interested in?"
                        placeholder={field.value?.length ? undefined : 'Select products'}
                        data={ProductInterestOptions.map((value) => ({ value, label: value }))}
                        error={getStep2Error(errors, 'productInterest')}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        hidePickedOptions={false}
                        comboboxProps={multiSelectComboboxProps}
                      />
                    )}
                  />
                  {showEventAttendance && (
                    <Controller
                      name="eventAttendance"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="How many events does your company attend per year?"
                          placeholder="Select event attendance"
                          data={EventAttendanceOptions.map((value) => ({ value, label: value }))}
                          error={getStep2Error(errors, 'eventAttendance')}
                          value={field.value ?? null}
                          onChange={(value) => field.onChange(value ?? undefined)}
                        />
                      )}
                    />
                  )}
                  {showDbcAmount && (
                    <Controller
                      name="dbcAmount"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="How many employees will need digital business cards?"
                          placeholder="Select number of cards"
                          data={DbcAmountOptions.map((value) => ({ value, label: value }))}
                          error={getStep2Error(errors, 'dbcAmount')}
                          value={field.value ?? null}
                          onChange={(value) => field.onChange(value ?? undefined)}
                        />
                      )}
                    />
                  )}
                  <Controller
                    name="hearAboutUs"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        label="How did you hear about us?"
                        placeholder={field.value?.length ? undefined : 'Select one or more'}
                        data={HearAboutUsOptions.map((value) => ({
                          value,
                          label: HearAboutUsLabels[value],
                        }))}
                        error={getStep2Error(errors, 'hearAboutUs')}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        hidePickedOptions={false}
                        comboboxProps={multiSelectComboboxProps}
                      />
                    )}
                  />
                </Stack>
              </motion.div>
            )}
          </AnimatePresence>

          {(errors.root && 'serverError' in errors.root && errors.root['serverError']?.message) ||
          serverError ? (
            <Alert color="red">{serverError ?? errors.root?.['serverError']?.message}</Alert>
          ) : null}

          <Button
            type="submit"
            fullWidth
            size="md"
            radius={9999}
            color="#1f1511"
            loading={submitLoading}
          >
            {submitLabel}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

type FormErrors = ReturnType<typeof useForm<ContactSalesData>>['formState']['errors'];

function getEmailError(errors: FormErrors): string | undefined {
  return errors.email?.message;
}

function getStep2Error(errors: FormErrors, key: string): string | undefined {
  const field = (errors as Record<string, { message?: string } | undefined>)[key];
  return field?.message;
}
